import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Thread, ThreadMessage } from "@/api/me";
import { useMarketLink } from "../links";
import { useV3 } from "../useV3";
import { Send } from "../Icons";
import { senderOf, textOf, whenOf, type Chat } from "./useThreads";

/* One conversation: its listing context, its messages, its composer.
 *
 * Shared by the inbox pane, the desktop popup and the mobile sheet, because
 * they ARE the same conversation and a second implementation would be a
 * second set of bugs about whose bubble is whose.
 *
 * DAY SEPARATORS come from the message timestamps, not from a stored field.
 *
 * FOUR SIGNALS ARE NOT DRAWN -- read ticks, typing, presence, per-thread
 * unread. The design wraps each in `[data-optional-feature] hidden` and says
 * to remove the attribute when the backend ships. None has shipped. A read
 * tick that means "we did not check" is worse than no tick.
 */
export function Conversation({ chat, thread }: { chat: Chat; thread: Thread }) {
  const { s, locale, dir } = useV3();
  const link = useMarketLink();
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const end = useRef<HTMLDivElement | null>(null);

  const messages: ThreadMessage[] = Array.isArray(thread.messages) ? thread.messages : [];
  const context = chat.contextOf(thread);

  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [messages.length, thread.id]);

  async function submit() {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    setFailed(false);
    setDraft("");
    const ok = await chat.send(thread.id, text);
    if (!ok) { setDraft(text); setFailed(true); }
    setSending(false);
  }

  const dayLabel = (iso: string | null): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();
    if (sameDay(d, today)) return s.chat.today;
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (sameDay(d, yesterday)) return s.chat.yesterday;
    return d.toLocaleDateString(locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB",
      { day: "numeric", month: "long" });
  };

  let lastDay: string | null = null;

  return (
    <div className="sq-thread">
      {context && (
        <Link to={link(`/listing/${encodeURIComponent(context.id)}`)}
          className="sq-thread__context" data-component="thread-context">
          {context.photo
            ? <img src={context.photo} alt="" loading="lazy" />
            : <span style={{ width: 56, height: 42, borderRadius: 6, background: "var(--sq-color-border)" }} />}
          <div style={{ minWidth: 0 }}>
            <strong>{context.title}</strong>
          </div>
          <span className="sq-link" style={{ fontSize: 13 }}>{s.chat.viewListing}</span>
        </Link>
      )}

      <div className="sq-thread__messages" role="log" aria-live="polite">
        {messages.map((m, i) => {
          const when = whenOf(m);
          const day = dayLabel(when);
          const showDay = day && day !== lastDay;
          if (day) lastDay = day;
          const mine = chat.isMine(m);
          return (
            <div key={`${senderOf(m)}-${when}-${i}`} style={{ display: "contents" }}>
              {showDay && <div className="sq-day" data-component="day-separator">{day}</div>}
              <div className={`sq-msg ${mine ? "sq-msg--mine" : "sq-msg--theirs"}`}>
                <div className="sq-msg__bubble">{textOf(m)}</div>
                <div className="sq-msg__meta">
                  {when && (
                    <time dateTime={when}>
                      {new Date(when).toLocaleTimeString(
                        locale === "ar" ? "ar-EG-u-nu-latn" : "en-GB",
                        { hour: "2-digit", minute: "2-digit" })}
                    </time>
                  )}
                  {/* No read tick: there is no read state on the server, and a
                      tick that means "we did not check" is worse than none. */}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>

      {failed && (
        <div className="sq-thread__blocked" role="alert">{s.chat.failed}</div>
      )}

      <form className="sq-thread__composer"
        onSubmit={(e) => { e.preventDefault(); void submit(); }}>
        <textarea rows={1} placeholder={s.chat.placeholder} aria-label={s.chat.message}
          value={draft} disabled={sending}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            /* Enter sends, Shift+Enter breaks the line -- the design's own
               hint, and the behaviour every messenger has. */
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void submit(); }
          }} />
        <button type="submit" className="sq-btn sq-btn--primary sq-btn--icon"
          style={{ height: 44, width: 44, borderRadius: "50%" }}
          aria-label={s.chat.send} disabled={sending || !draft.trim()}>
          <Send rtl={dir === "rtl"} />
        </button>
      </form>
      <div className="sq-thread__hint">{s.chat.hint}</div>
    </div>
  );
}
